import React from 'react';
import styled from '@emotion/styled';
import { AdvancedImage, lazyload, placeholder, responsive } from '@cloudinary/react';
import { TextWithImage } from '@naf-simba/sections-text-with-image';
import { TextAndImageBlockType } from '@appTypes/TextAndImageBlockType';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import BlockContent from '@components/block-content/BlockContent';
import { groupAccordionRuns } from './TextAndImageBlock.utils';

export const TextAndImageBlock = ({ block }: { block: TextAndImageBlockType }) => {
  const { getResponsiveImage } = useCloudinary();
  // Width is required: `getResponsiveImage` skips `.resize()` without one and Cloudinary serves
  // the original (6.6 MB here). 600 is the 544 CSS px cap plus headroom — `dpr()` multiplies it.
  const cldImage = getResponsiveImage(block?.image?.publicId, { width: 600 });

  const advancedImage = cldImage ? (
    <AdvancedImage
      style={{ maxWidth: '100%' }}
      alt={block?.image?.alt || block?.image?.altText}
      cldImg={cldImage}
      plugins={[
        lazyload({ rootMargin: '10px 20px 10px 30px', threshold: 0.25 }),
        responsive({ steps: 200 }),
        placeholder({ mode: 'blur' }),
      ]}
    />
  ) : undefined;

  return (
    <StyledTextWithImage
      contentAlignment={block.textContentAlignment === 'center' ? 'center' : 'start'}
      title={block.title}
      className="TextAndImageBlock"
      media={advancedImage ? { render: () => advancedImage } : undefined}
      imagePosition={block.imageAlignment?.horizontal === 'left' ? 'start' : 'end'}
      mobileImagePosition={block.imageAlignment?.vertical === 'above' ? 'start' : 'end'}
      imageWidth={block.imageAlignment?.relativeSize}
    >
      {groupAccordionRuns(block.text).map((run) =>
        run.isAccordions ? (
          <div key={run.key}>
            <BlockContent value={run.blocks} />
          </div>
        ) : (
          <BlockContent key={run.key} value={run.blocks} />
        ),
      )}
    </StyledTextWithImage>
  );
};

// TextWithImage stacks its content in a flex column, which stretches a standalone link to the full
// width, and the simba Link centres its content — so keep it at its own width, on the left.
const StyledTextWithImage = styled(TextWithImage)`
  a {
    align-self: flex-start;
  }
`;
